import { useCallback, useEffect, useRef, useState } from "react";

const configuredApiBase =
  typeof process !== "undefined" && process.env
    ? process.env.REACT_APP_GOOURHUB_BUSINESS_API
    : "";

const BUSINESS_API = (
  configuredApiBase || "https://business-api.goourhub.com"
).replace(/\/+$/, "");

const HUBMART_API = "https://api.goourhub.com:435";
const MAX_PER_GROUP = 4;
const MAX_BUSINESS_RESULTS = 10;

const HUBMART_CATEGORY_IDS = {
  free: 0,
  vehicle: 1,
  mobile: 2,
  computer: 3,
  job: 4,
  cloth: 5,
  rent: 6,
  house: 7,
  toys: 8,
  books: 9,
  other: 10,
  service: 11,
  traditional: 12,
};

const EMPTY_RESULTS = {
  businesses: [],
  events: [],
  deals: [],
  organizations: [],
  hubmart: [],
};

const INITIAL_STATE = {
  status: "idle",
  results: EMPTY_RESULTS,
  message: "",
  filters: null,
  error: "",
};

const STOP_WORDS = new Set([
  "a", "an", "and", "are", "at", "be", "business", "businesses", "can",
  "deal", "deals", "do", "event", "events", "find", "for", "free", "from",
  "give", "help", "hub", "hubmart", "i", "in", "is", "item", "items", "local",
  "looking", "me", "my", "near", "nonprofit", "nonprofits", "of", "on", "or",
  "organization", "organizations", "please", "san", "show", "something", "that",
  "the", "this", "to", "today", "tomorrow", "tonight", "want", "week", "weekend",
  "what", "where", "with", "you", "your", "antonio",
]);

const clean = (value) => String(value || "").trim();

const objectId = (value) => {
  if (!value) return "";
  if (typeof value === "object") {
    return clean(value.$oid || value.oid || value.id || value._id);
  }
  return clean(value);
};

const pickArray = (payload, keys) => {
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
    if (Array.isArray(payload?.data?.[key])) return payload.data[key];
  }
  return [];
};

const searchableText = (value, output = []) => {
  if (value === null || value === undefined) return output;
  if (typeof value === "string" || typeof value === "number") {
    output.push(String(value));
    return output;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => searchableText(item, output));
    return output;
  }
  if (typeof value === "object") {
    Object.values(value).forEach((item) => searchableText(item, output));
  }
  return output;
};

const queryTokens = (query, filters) => {
  const supplied = Array.isArray(filters?.keywords) ? filters.keywords : [];
  const source = supplied.length ? supplied.join(" ") : query;
  const tokens = source
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .split(/\s+/)
    .filter((token) => token.length > 1 && !STOP_WORDS.has(token));

  const community = clean(filters?.community).toLowerCase();
  if (community) tokens.push(community);

  return Array.from(new Set(tokens)).slice(0, 8);
};

const relevant = (records, tokens, limit = MAX_PER_GROUP) => {
  if (!tokens.length) return records.slice(0, limit);

  return records
    .map((record, index) => {
      const haystack = searchableText(record).join(" ").toLowerCase();
      const score = tokens.reduce(
        (total, token) => total + (haystack.includes(token) ? 1 : 0),
        0
      );
      return { record, score, index };
    })
    .filter((entry) => entry.score > 0)
    .sort((left, right) => right.score - left.score || left.index - right.index)
    .slice(0, limit)
    .map((entry) => entry.record);
};

const fetchJson = async (url, signal) => {
  const response = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
    signal,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(`Hub source unavailable (${response.status}).`);
  return payload;
};

const chicagoDateParts = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const get = (type) => Number(parts.find((part) => part.type === type)?.value);
  return { year: get("year"), month: get("month"), day: get("day") };
};

const addCalendarDays = (parts, amount) => {
  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day + amount, 12));
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  };
};

const isoDate = ({ year, month, day }) =>
  `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;

const eventInWindow = (event, timing) => {
  if (!timing || timing === "any" || timing === "upcoming") return true;
  const eventDate = clean(event?.event_date || event?.date).slice(0, 10);
  if (!eventDate) return false;

  const today = chicagoDateParts();
  if (timing === "today") return eventDate === isoDate(today);
  if (timing === "tomorrow") return eventDate === isoDate(addCalendarDays(today, 1));

  if (timing === "this_week") {
    return eventDate >= isoDate(today) && eventDate <= isoDate(addCalendarDays(today, 6));
  }

  const dayOfWeek = new Date(
    Date.UTC(today.year, today.month - 1, today.day, 12)
  ).getUTCDay();
  const saturdayOffset = dayOfWeek === 0 ? -1 : (6 - dayOfWeek + 7) % 7;
  const saturday = addCalendarDays(today, saturdayOffset);
  const sunday = addCalendarDays(saturday, 1);
  return eventDate >= isoDate(saturday) && eventDate <= isoDate(sunday);
};

const isFreeRecord = (record) => {
  if (record?.is_free === true || record?.free === true) return true;
  const value = record?.price ?? record?.cost ?? record?.admission_fee;
  if (value !== undefined && value !== null && Number(value) === 0) return true;
  return /(^|\W)free(\W|$)/i.test(
    [record?.cost_label, record?.admission, record?.price_label, record?.description]
      .filter(Boolean)
      .join(" ")
  );
};

const normalizeDeal = (offer) => {
  const id = objectId(offer?.id || offer?._id) || clean(offer?.title);
  return {
    key: `deal-${id}`,
    kind: "deal",
    title: offer?.business_name || offer?.title || "Local business",
    subtitle: offer?.business_name ? offer?.title || "Special offer" : "Special offer",
    description: offer?.short_description || "",
    badge: offer?.offer_text || "Deal",
    image: offer?.image_url || "",
    dateStart: offer?.start_date || clean(offer?.created_at).slice(0, 10),
    dateEnd: offer?.end_date || "",
    meta: offer?.end_date ? `Ends ${offer.end_date}` : "Limited time",
    secondary: offer?.promo_phrase ? `Use: ${offer.promo_phrase}` : "",
    place: offer?.city || offer?.location || "San Antonio",
    free: false,
    href: "",
    actionLabel: "",
    source: offer,
  };
};

const hubMartId = (item) =>
  objectId(
    item?.postid || item?.postId || item?.adid || item?.adId || item?.recordId ||
      item?._id || item?.id || item?.ID
  );

const resultTypes = (filters) =>
  new Set(
    Array.isArray(filters?.resultTypes) && filters.resultTypes.length
      ? filters.resultTypes
      : ["businesses"]
  );

export function useAiHubSearch() {
  const [result, setResult] = useState(INITIAL_STATE);
  const requestRef = useRef(null);

  const reset = useCallback(() => {
    requestRef.current?.abort();
    requestRef.current = null;
    setResult(INITIAL_STATE);
  }, []);

  const search = useCallback(async (rawQuery) => {
    const query = clean(rawQuery);
    if (query.length < 3) {
      setResult({
        ...INITIAL_STATE,
        error: "Please describe what you are looking for.",
        status: "error",
      });
      return null;
    }

    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setResult({
      ...INITIAL_STATE,
      status: "loading",
      message: "Understanding your request…",
    });

    const timeoutId = window.setTimeout(() => controller.abort(), 40000);

    try {
      const aiResponse = await fetch(`${BUSINESS_API}/api/ai/hub-search`, {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ query, city: "San Antonio" }),
        signal: controller.signal,
      });
      const aiPayload = await aiResponse.json().catch(() => ({}));
      if (!aiResponse.ok || aiPayload?.success === false) {
        throw new Error(aiPayload?.message || `AI search is unavailable (${aiResponse.status}).`);
      }

      const filters = aiPayload?.filters || {};
      const wanted = resultTypes(filters);
      const tokens = queryTokens(query, filters);
      const sourceTasks = [];

      if (wanted.has("events")) {
        sourceTasks.push(
          fetchJson(`${BUSINESS_API}/api/events/upcoming?limit=50`, controller.signal)
            .then((payload) => ({ type: "events", rows: pickArray(payload, ["events"]) }))
        );
      }
      if (wanted.has("deals")) {
        sourceTasks.push(
          fetchJson(`${BUSINESS_API}/api/offers?limit=40`, controller.signal)
            .then((payload) => ({ type: "deals", rows: pickArray(payload, ["offers"]) }))
        );
      }
      if (wanted.has("organizations")) {
        sourceTasks.push(
          fetchJson(`${BUSINESS_API}/api/organizations`, controller.signal)
            .then((payload) => ({
              type: "organizations",
              rows: pickArray(payload, ["organizations"]),
            }))
        );
      }
      if (wanted.has("hubmart")) {
        const params = new URLSearchParams({ ct: "1", limit: "30" });
        const category = clean(filters?.hubmartCategory).toLowerCase();
        if (category in HUBMART_CATEGORY_IDS) {
          params.set("category", String(HUBMART_CATEGORY_IDS[category]));
        }
        params.set("sentence", query);
        sourceTasks.push(
          fetchJson(`${HUBMART_API}/search?${params.toString()}`, controller.signal)
            .then((payload) => ({
              type: "hubmart",
              rows: pickArray(payload, ["results", "result", "items"]),
            }))
        );
      }

      const settled = await Promise.allSettled(sourceTasks);
      const sourceRows = Object.fromEntries(
        settled
          .filter((entry) => entry.status === "fulfilled")
          .map((entry) => [entry.value.type, entry.value.rows])
      );

      const events = (sourceRows.events || [])
        .filter((event) => eventInWindow(event, filters?.eventTiming))
        .filter((event) => !filters?.freeOnly || isFreeRecord(event));
      const deals = (sourceRows.deals || [])
        .map(normalizeDeal)
        .filter((deal) => !filters?.freeOnly || isFreeRecord(deal.source));
      const organizations = sourceRows.organizations || [];
      const hubmart = (sourceRows.hubmart || [])
        .filter((item) => !filters?.freeOnly || isFreeRecord(item))
        .map((item) => ({ ...item, id: hubMartId(item) || objectId(item?.id) }));

      const nextResult = {
        status: "ready",
        results: {
          businesses: Array.isArray(aiPayload?.businesses)
            ? aiPayload.businesses.slice(0, MAX_BUSINESS_RESULTS)
            : [],
          events: relevant(events, tokens),
          deals: relevant(deals, tokens),
          organizations: relevant(organizations, tokens),
          hubmart: relevant(hubmart, tokens),
        },
        message: clean(aiPayload?.message) || "Here are the closest matches across GoOurHub.",
        filters,
        error: "",
      };

      setResult(nextResult);
      return nextResult;
    } catch (error) {
      const message =
        error?.name === "AbortError"
          ? "AI search took too long. Please try again."
          : error?.message || "AI search is temporarily unavailable.";
      setResult({ ...INITIAL_STATE, status: "error", error: message });
      return null;
    } finally {
      window.clearTimeout(timeoutId);
      if (requestRef.current === controller) requestRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      requestRef.current?.abort();
    },
    []
  );

  return { ...result, search, reset };
}

export default useAiHubSearch;
